import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  LinearProgress,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from '@mui/material';
import {Visibility, VisibilityOff, Warning, Delete} from '@mui/icons-material';
import {ReactNode, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  User,
  UpdatePasswordReq,
  UserRequestOrgDeletionReq,
  UserConfirmOrgDeletionReq,
} from 'src/apis/autoGenerate/types/user';
import {useAppSelector, useAppDispatch} from 'src/redux/hooks';
import {selectAccount, setUserDetail} from 'src/redux/slice/accountSlice';
import message from 'src/components/Message';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  orgServiceUpdate,
  userServiceUpdatePassword,
  userServiceRequestOrgDeletion,
  userServiceConfirmOrgDeletion,
} from 'src/apis/autoGenerate/services';
import {useOrgId} from 'src/hooks';
import {useAuth} from 'src/components/AuthProvider.tsx';
import StartPagePreference from 'src/components/StartPagePreference';

interface ShowPasswordState {
  oldPassword: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

interface PasswordStrength {
  score: number; // 0-100
  messages: string[];
}

function Container({children}: {children: ReactNode}) {
  return (
    <Box
      sx={{
        width: 600,
        border: '1px solid rgba(217, 217, 217, 0.35)',
        borderRadius: 2,
        padding: 3,
        marginBottom: 3,
      }}
    >
      {children}
    </Box>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAccount);
  const {setToken} = useAuth();
  const [user, setUser] = useState<User>({firstName: '', lastName: '', email: ''});
  const [business, setBusiness] = useState({
    name: '',
    desc: '',
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState<ShowPasswordState>({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    messages: [],
  });
  const {orgId} = useOrgId();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [passwordEditMode, setPasswordEditMode] = useState(false);

  // Organization deletion states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);
  const [deletionRequested, setDeletionRequested] = useState(false);

  // Check if current user is organization owner
  const isOrgOwner = () => {
    if (!account.userDetail?.organizations || !orgId) return false;
    const currentOrg = account.userDetail.organizations.find(org => org.id === orgId);
    return currentOrg?.ownerId === account.userDetail?.id;
  };

  // Load user data from Redux store
  useEffect(() => {
    if (account.userDetail) {
      setUser({
        firstName: account.userDetail.firstName || '',
        lastName: account.userDetail.lastName || '',
        email: account.userDetail.email || '',
      });

      // If user has organizations, load the first one's data
      if (account.userDetail.organizations && account.userDetail.organizations.length > 0) {
        const org = account.userDetail.organizations[0];
        setBusiness({
          name: org.name || '',
          desc: org.desc || '',
        });
      }
    }
  }, [account.userDetail]);

  const checkPasswordStrength = (password: string): PasswordStrength => {
    const strength: PasswordStrength = {
      score: 0,
      messages: [],
    };

    // Password length check
    if (password.length < 8) {
      strength.messages.push('Password must be at least 8 characters');
    } else {
      strength.score += 25;
    }

    // Contains number check
    if (!/\d/.test(password)) {
      strength.messages.push('Include at least one number');
    } else {
      strength.score += 25;
    }

    // Contains lowercase letter check
    if (!/[a-z]/.test(password)) {
      strength.messages.push('Include at least one lowercase letter');
    } else {
      strength.score += 25;
    }

    // Contains uppercase letter check
    if (!/[A-Z]/.test(password)) {
      strength.messages.push('Include at least one uppercase letter');
    } else {
      strength.score += 25;
    }

    return strength;
  };

  const getStrengthColor = (score: number) => {
    if (score <= 25) return '#f44336'; // Red
    if (score <= 50) return '#ff9800'; // Orange
    if (score <= 75) return '#ffc107'; // Yellow
    return '#4caf50'; // Green
  };

  const handleClickShowPassword = (key: keyof ShowPasswordState) =>
    setShowPassword({...showPassword, [key]: !showPassword[key]});

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setPasswordData({...passwordData, newPassword});
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleUpdateBusinessProfile = () => {
    if (!orgId) {
      message.error('Organization ID not found');
      return;
    }

    setLoading(true);

    orgServiceUpdate(orgId, {
      name: business.name,
      desc: business.desc || '',
    })
      .then(() => {
        message.success('Business profile updated successfully');
        setEditMode(false);
      })
      .catch(err => {
        message.error(err.message || 'Failed to update business profile');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdatePassword = () => {
    if (!account.userDetail?.id) {
      message.error('User ID not found');
      return;
    }

    // Validation
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      message.error('All password fields are required');
      return;
    }

    // Password strength validation
    if (passwordStrength.score < 100) {
      message.error('Please ensure your new password meets all requirements');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      message.error('New password and confirm password do not match');
      return;
    }

    setPasswordLoading(true);

    const updatePasswordReq: UpdatePasswordReq = {
      userId: account.userDetail.id,
      oldPassword: passwordData.oldPassword,
      newPassword: passwordData.newPassword,
      isEncrypted: false,
    };

    userServiceUpdatePassword(account.userDetail.id, updatePasswordReq)
      .then(() => {
        message.success('Password updated successfully. Please log in again for security.');

        // Clear all authentication data for security
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');

        // Redirect to login page
        setTimeout(() => {
          navigate('/login', {replace: true});
        }, 2000);
      })
      .catch(err => {
        message.error(err.response?.data?.message || err.message || 'Failed to update password');
      })
      .finally(() => {
        setPasswordLoading(false);
      });
  };

  const handleRequestOrgDeletion = () => {
    setDeleteLoading(true);

    // No need to pass organizationId - backend extracts from token
    const requestData: UserRequestOrgDeletionReq = {};

    userServiceRequestOrgDeletion(requestData)
      .then(response => {
        message.success(response.data.message);
        setDeletionRequested(true);
        setDeleteDialogOpen(false);
        setConfirmDeleteDialogOpen(true);
      })
      .catch(err => {
        message.error(
          err.response?.data?.message || err.message || 'Failed to request organization deletion'
        );
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleConfirmOrgDeletion = () => {
    if (!verificationCode.trim()) {
      message.error('Please enter the verification code');
      return;
    }

    setConfirmDeleteLoading(true);

    // No need to pass organizationId - backend extracts from token
    const confirmData: UserConfirmOrgDeletionReq = {
      verificationCode: verificationCode.trim(),
    };

    userServiceConfirmOrgDeletion(confirmData)
      .then(response => {
        message.success(response.data.message);

        // Clear all authentication data
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');

        // Redirect to login page after successful deletion
        setTimeout(() => {
          navigate('/login', {replace: true});
        }, 2000);
      })
      .catch(err => {
        message.error(
          err.response?.data?.message || err.message || 'Failed to confirm organization deletion'
        );
      })
      .finally(() => {
        setConfirmDeleteLoading(false);
      });
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const togglePasswordEditMode = () => {
    setPasswordEditMode(!passwordEditMode);
    if (!passwordEditMode) {
      // Reset password fields when entering edit mode
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordStrength({
        score: 0,
        messages: [],
      });
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleCloseConfirmDeleteDialog = () => {
    setConfirmDeleteDialogOpen(false);
    setVerificationCode('');
    setDeletionRequested(false);
  };

  return (
    <Box>
      <Container>
        <Typography variant="subtitle2" sx={{mb: 2}}>
          Account Settings
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              id="firstName"
              label="First Name"
              variant="standard"
              value={user.firstName}
              disabled={true}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              id="lastName"
              label="Last Name"
              variant="standard"
              value={user.lastName}
              disabled={true}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          required
          id="email"
          label="Email"
          variant="standard"
          value={user.email}
          disabled={true}
          InputProps={{
            readOnly: true,
          }}
          sx={{mb: 2}}
        />
        <Box sx={{mt: 1}}>
          <StartPagePreference />
        </Box>
      </Container>

      <Container>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
          <Typography variant="subtitle2">Change Password</Typography>
          {!passwordEditMode && (
            <Button variant="outlined" size="small" onClick={togglePasswordEditMode}>
              Change Password
            </Button>
          )}
        </Box>
        {passwordEditMode && (
          <>
            <FormControl fullWidth variant="outlined" sx={{mb: 2}}>
              <InputLabel htmlFor="oldPassword">Current Password *</InputLabel>
              <OutlinedInput
                id="oldPassword"
                type={showPassword.oldPassword ? 'text' : 'password'}
                value={passwordData.oldPassword}
                onChange={e => setPasswordData({...passwordData, oldPassword: e.target.value})}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('oldPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.oldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Current Password *"
              />
            </FormControl>

            <FormControl fullWidth variant="outlined" sx={{mb: 1}}>
              <InputLabel htmlFor="newPassword">New Password *</InputLabel>
              <OutlinedInput
                id="newPassword"
                type={showPassword.newPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={handleNewPasswordChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('newPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="New Password *"
              />
            </FormControl>

            {/* Password strength indicator */}
            {passwordData.newPassword && (
              <Box sx={{mb: 2}}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength.score}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getStrengthColor(passwordStrength.score),
                    },
                  }}
                />

                {/* Password requirements */}
                <Box sx={{mt: 1}}>
                  {passwordStrength.messages.map((msg, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      • {msg}
                    </Typography>
                  ))}
                  {passwordStrength.score === 100 && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      display="block"
                      sx={{mt: 0.5}}
                    >
                      Password strength: Strong
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <FormControl fullWidth variant="outlined" sx={{mb: 2}}>
              <InputLabel htmlFor="confirmPassword">Confirm New Password *</InputLabel>
              <OutlinedInput
                id="confirmPassword"
                type={showPassword.confirmPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleClickShowPassword('confirmPassword')}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Confirm New Password *"
              />
            </FormControl>

            <Box sx={{display: 'flex', gap: 2}}>
              <LoadingButton
                loading={passwordLoading}
                variant="contained"
                onClick={handleUpdatePassword}
                disabled={passwordStrength.score < 100} // Disable if password doesn't meet requirements
              >
                Update Password
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={togglePasswordEditMode}
                disabled={passwordLoading}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Container>

      <Container>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
          <Typography variant="subtitle2">Business Profile</Typography>
          {!editMode && (
            <Button variant="outlined" size="small" onClick={toggleEditMode}>
              Edit
            </Button>
          )}
        </Box>
        <TextField
          fullWidth
          id="businessName"
          label="Business Name"
          variant="standard"
          value={business.name}
          onChange={e => setBusiness({...business, name: e.target.value})}
          disabled={!editMode}
          InputProps={{
            readOnly: !editMode,
          }}
          sx={{mb: 2}}
        />
        <TextField
          fullWidth
          id="description"
          label="Description"
          variant="standard"
          multiline
          rows={2}
          value={business.desc}
          onChange={e => setBusiness({...business, desc: e.target.value})}
          disabled={!editMode}
          InputProps={{
            readOnly: !editMode,
          }}
          sx={{mb: 2}}
        />
        {editMode && (
          <Box sx={{display: 'flex', gap: 2}}>
            <LoadingButton
              loading={loading}
              variant="contained"
              onClick={handleUpdateBusinessProfile}
            >
              Update
            </LoadingButton>
            <Button variant="outlined" onClick={() => setEditMode(false)} disabled={loading}>
              Cancel
            </Button>
          </Box>
        )}
      </Container>

      {/* Organization Deletion Section - Only visible to organization owners */}
      {isOrgOwner() && (
        <Container>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
            <Typography variant="subtitle2" color="error">
              Danger Zone
            </Typography>
          </Box>

          <Alert severity="warning" sx={{mb: 2}}>
            <Typography variant="body2">
              <strong>Warning:</strong> Deleting your organization is permanent and cannot be
              undone. All data associated with this organization will be permanently deleted.
            </Typography>
          </Alert>

          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
            <Chip
              label="Organization Owner"
              color="primary"
              size="small"
              sx={{fontWeight: 'bold'}}
            />
            <Typography variant="body2" color="text.secondary">
              Only organization owners can delete the organization
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{
              borderColor: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                backgroundColor: 'error.light',
              },
            }}
          >
            Delete Organization
          </Button>
        </Container>
      )}

      {/* Initial Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Warning color="error" />
          Confirm Organization Deletion
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{mb: 2}}>
            <Typography variant="body2">
              <strong>This action cannot be undone!</strong>
            </Typography>
          </Alert>

          <Typography variant="body1" sx={{mb: 2}}>
            You are about to delete the organization "<strong>{business.name}</strong>".
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            This will permanently delete:
          </Typography>

          <Box component="ul" sx={{pl: 2, mb: 2}}>
            <Typography component="li" variant="body2" color="text.secondary">
              All organization data and settings
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              All user accounts associated with this organization
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              All devices, properties, and related data
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              All access logs and historical data
            </Typography>
          </Box>

          <Typography variant="body2" sx={{mb: 2}}>
            If you proceed, we will send a verification email to <strong>{user.email}</strong>
            with a confirmation code to complete the deletion process.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <LoadingButton
            loading={deleteLoading}
            onClick={handleRequestOrgDeletion}
            color="error"
            variant="contained"
          >
            Send Verification Email
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Email Verification Dialog */}
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={handleCloseConfirmDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Verification Code</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{mb: 2}}>
            <Typography variant="body2">
              A verification email has been sent to <strong>{user.email}</strong>. Please check your
              email and enter the verification code below.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={e => setVerificationCode(e.target.value)}
            placeholder="Enter the code from your email"
            variant="outlined"
            sx={{mt: 2}}
            inputProps={{
              style: {textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2em'},
            }}
          />

          <Typography variant="body2" color="text.secondary" sx={{mt: 2, textAlign: 'center'}}>
            The verification code expires in 30 minutes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDeleteDialog} disabled={confirmDeleteLoading}>
            Cancel
          </Button>
          <LoadingButton
            loading={confirmDeleteLoading}
            onClick={handleConfirmOrgDeletion}
            color="error"
            variant="contained"
            disabled={!verificationCode.trim()}
          >
            Confirm Deletion
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
